import { Component, ChangeDetectionStrategy, ViewChild, inject, signal, DestroyRef, effect, untracked } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { TranslateModule } from "@ngx-translate/core";

import { StepperModule, Stepper } from "primeng/stepper";
import { ButtonModule } from "primeng/button";
import { SelectModule } from "primeng/select";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { InputTextModule } from "primeng/inputtext";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { distinctUntilChanged, map } from "rxjs";

import { AttioDealOption, DuplicateQuote, DuplicationType, SalesPerson } from "src/api-service";
import { selectCurrentUserSalesPerson, selectSalesPersonsForCurrentCompany } from "src/app/store/application/data/sales-person/sales-persons/sales-persons.selectors";
import { selectQuoteId, selectQuoteDisplayId, selectQuoteAttioDealRecordId } from "src/app/store/pages/quote/quote.selectors";
import { selectVersionId, selectVersionName } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { SelectAttioDealComponent, OPPORTUNITY_NAME_MAX_LENGTH, dealOpportunityName } from "src/app/shared/components/select-attio-deal/select-attio-deal.component";

@Component({
    selector: "app-duplicate-quote",
    templateUrl: "./duplicate-quote.component.html",
    styleUrls: ["./duplicate-quote.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        StepperModule,
        ButtonModule,
        SelectModule,
        ProgressSpinnerModule,
        InputTextModule,
        FloatLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        CoreErrorsComponent,
        SelectAttioDealComponent,
    ],
})
export class DuplicateQuoteComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly store = inject(Store);
    private readonly dialogRef = inject(DynamicDialogRef);
    private readonly destroyRef = inject(DestroyRef);

    // =========================================================================
    // VIEW CHILDREN
    // =========================================================================
    @ViewChild("stepper", { static: true }) public stepper!: Stepper;

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quoteId = toSignal(this.store.select(selectQuoteId));
    public readonly quoteDisplayId = toSignal(this.store.select(selectQuoteDisplayId));
    private readonly selectedVersionId = toSignal(this.store.select(selectVersionId));
    public readonly selectedVersionName = toSignal(this.store.select(selectVersionName));
    public readonly salesPersons = toSignal(this.store.select(selectSalesPersonsForCurrentCompany), { initialValue: [] });
    private readonly currentUserSalesPerson = toSignal(this.store.select(selectCurrentUserSalesPerson), { initialValue: null });
    /** The duplicated quote's deal, offered first for the copy. */
    public readonly sourceDealRecordId = toSignal(this.store.select(selectQuoteAttioDealRecordId));

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly activeStep = signal(1);
    public readonly isSubmitting = signal(false);

    // =========================================================================
    // FORM STATE
    // =========================================================================
    /** Picked first: the deals offered for the copy are the salesperson's. */
    public readonly salesPersonControl = new FormControl<SalesPerson | null>(null, Validators.required);
    public readonly salesPerson = toSignal(this.salesPersonControl.valueChanges, { initialValue: null });

    public readonly stepSalesPerson = new FormGroup({
        salesPerson: this.salesPersonControl,
    });

    /** Every quote created in Core is linked to an Attio deal, copies included, and the deal decides the copy's customer. */
    public readonly dealControl = new FormControl<AttioDealOption | null>(null, Validators.required);

    public readonly stepDeal = new FormGroup({
        deal: this.dealControl,
        newOpportunityName: new FormControl("", [Validators.required, Validators.maxLength(OPPORTUNITY_NAME_MAX_LENGTH)]),
    });

    public readonly form = new FormGroup({
        stepSalesPerson: this.stepSalesPerson,
        stepDeal: this.stepDeal,
    });

    // =========================================================================
    // EFFECTS
    // =========================================================================
    // Starts as the logged-in user when they are a salesperson here. Once only, so clearing the field keeps it clear.
    private salesPersonPrefilled = false;
    private readonly prefillSalesPersonEffect = effect(() => {
        const current = this.currentUserSalesPerson();
        if (current && !this.salesPersonPrefilled) {
            this.salesPersonPrefilled = true;
            untracked(() => {
                if (!this.salesPersonControl.value) {
                    this.salesPersonControl.setValue(current);
                }
            });
        }
    });

    // =========================================================================
    // CONSTRUCTOR
    // =========================================================================
    constructor() {
        this.initFormListeners();
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onSubmit(): void {
        this.isSubmitting.set(true);

        const duplicateQuote: DuplicateQuote = {
            duplicationType: DuplicationType.Full,
            versionId: this.selectedVersionId(),
            customerCode: this.dealControl.value?.customerCode,
            salesPersonId: this.salesPersonControl.value?.id,
            salesforceOpportunityName: this.stepDeal.get("newOpportunityName")?.value ?? "",
            attioDealRecordId: this.dealControl.value?.recordId,
        };

        const quoteId = this.quoteId();
        if (quoteId) {
            this.store.dispatch(QuoteActions.duplicateQuote({
                quoteId,
                request: duplicateQuote,
                dialogKey: 'DUPLICATE_QUOTE',
            }));
        }

        this.dialogRef.close();
    }

    public nextStep(): void {
        this.activeStep.update(step => step + 1);
    }

    public prevStep(): void {
        this.activeStep.update(step => step - 1);
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    private initFormListeners(): void {
        // The deal picker is only shown on its own step, so it can't see the salesperson change; a deal picked for
        // another salesperson is cleared here instead.
        this.salesPersonControl.valueChanges
            .pipe(map(salesPerson => salesPerson?.id ?? null), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.dealControl.setValue(null));

        // The opportunity name starts as the deal's name, and can still be changed.
        this.dealControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(deal => {
                if (deal?.name) {
                    this.stepDeal.get("newOpportunityName")?.setValue(dealOpportunityName(deal));
                }
            });
    }
}
